import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { createTransaction, getTransactions, deleteTransaction, updateTransaction } from '../services/transactionService';
import { getCategories } from '../services/categoryService';
import { getAccounts, updateAccountBalance } from '../services/accountService';
import './Transactions.css';

const Transactions = () => {
  const { currentUser } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [formData, setFormData] = useState({
    type: 'egreso',
    amount: '',
    category: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    accountId: ''
  });

  useEffect(() => {
    loadData();
  }, [currentUser]);

  const loadData = async () => {
    if (currentUser) {
      const txs = await getTransactions(currentUser.uid);
      const cats = await getCategories(currentUser.uid);
      const accs = await getAccounts(currentUser.uid);
      setTransactions(txs);
      setCategories(cats);
      setAccounts(accs);
      
      // Establecer la primera categoría de egreso como default
      const firstEgreso = cats.find(c => c.type === 'egreso');
      const firstAccount = accs[0];
      if (firstEgreso && !formData.category) {
        setFormData(prev => ({ 
          ...prev, 
          category: firstEgreso.name,
          accountId: firstAccount?.id || ''
        }));
      }
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(formData.amount);
    
    if (editingTransaction) {
      // Si está editando, revertir el balance anterior y aplicar el nuevo
      if (editingTransaction.accountId) {
        // Revertir transacción anterior
        await updateAccountBalance(
          editingTransaction.accountId, 
          editingTransaction.amount, 
          editingTransaction.type === 'ingreso' ? 'egreso' : 'ingreso'
        );
      }
      
      // Aplicar nueva transacción
      if (formData.accountId) {
        await updateAccountBalance(formData.accountId, amount, formData.type);
      }
      
      await updateTransaction(editingTransaction.id, {
        ...formData,
        amount
      });
    } else {
      // Nueva transacción
      await createTransaction(currentUser.uid, {
        ...formData,
        amount
      });
      
      // Actualizar balance de la cuenta
      if (formData.accountId) {
        await updateAccountBalance(formData.accountId, amount, formData.type);
      }
    }
    
    const firstCategory = categories.find(c => c.type === 'egreso');
    const firstAccount = accounts[0];
    setFormData({
      type: 'egreso',
      amount: '',
      category: firstCategory?.name || '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      accountId: firstAccount?.id || ''
    });
    setShowForm(false);
    setEditingTransaction(null);
    loadData();
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      type: transaction.type,
      amount: transaction.amount.toString(),
      category: transaction.category,
      description: transaction.description,
      date: transaction.date,
      accountId: transaction.accountId || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (transaction) => {
    if (window.confirm('¿Eliminar esta transacción?')) {
      // Revertir el balance de la cuenta
      if (transaction.accountId) {
        await updateAccountBalance(
          transaction.accountId, 
          transaction.amount, 
          transaction.type === 'ingreso' ? 'egreso' : 'ingreso'
        );
      }
      
      await deleteTransaction(transaction.id);
      loadData();
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTransaction(null);
    const firstCategory = categories.find(c => c.type === 'egreso');
    const firstAccount = accounts[0];
    setFormData({
      type: 'egreso',
      amount: '',
      category: firstCategory?.name || '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      accountId: firstAccount?.id || ''
    });
  };

  const handleTypeChange = (newType) => {
    const firstCategory = categories.find(c => c.type === newType);
    setFormData({
      ...formData,
      type: newType,
      category: firstCategory?.name || ''
    });
  };

  const getAccountName = (accountId) => {
    const account = accounts.find(a => a.id === accountId);
    return account ? account.name : 'Sin cuenta';
  };

  const filteredCategories = categories.filter(c => c.type === formData.type);



  return (
    <div className="transactions-page">
      <header>
        <h1>📝 Transacciones</h1>
        <button onClick={() => setShowForm(!showForm)} className="add-btn">
          {showForm ? 'Cancelar' : '+ Nueva'}
        </button>
      </header>

      {showForm && (
        <form onSubmit={handleSubmit} className="transaction-form">
          <h3>{editingTransaction ? 'Editar Transacción' : 'Nueva Transacción'}</h3>
          <select
            value={formData.type}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            <option value="ingreso">Ingreso</option>
            <option value="egreso">Egreso</option>
          </select>

          <input
            type="number"
            step="0.01"
            placeholder="Monto"
            value={formData.amount}
            onChange={(e) => setFormData({...formData, amount: e.target.value})}
            required
          />

          <select
            value={formData.category}
            onChange={(e) => setFormData({...formData, category: e.target.value})}
            required
          >
            {filteredCategories.length === 0 ? (
              <option value="">No hay categorías - Crea una en Categorías</option>
            ) : (
              filteredCategories.map(cat => (
                <option key={cat.id || cat.name} value={cat.name}>
                  {cat.icon} {cat.name}
                </option>
              ))
            )}
          </select>

          <input
            type="text"
            placeholder="Descripción"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
          />

          <input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({...formData, date: e.target.value})}
            required
          />

          <select
            value={formData.accountId}
            onChange={(e) => setFormData({...formData, accountId: e.target.value})}
            required
          >
            <option value="">Selecciona una cuenta</option>
            {accounts.map(account => (
              <option key={account.id} value={account.id}>
                {account.name} - ${account.balance?.toFixed(2) || '0.00'}
              </option>
            ))}
          </select>

          <div className="form-buttons">
            <button type="submit" className="save-btn">
              {editingTransaction ? 'Actualizar' : 'Guardar'}
            </button>
            <button type="button" onClick={handleCancel} className="cancel-btn">
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="transactions-list">
        {transactions.length === 0 ? (
          <div className="empty-state">
            <p>No hay transacciones registradas</p>
            <p className="hint">Crea tu primera transacción usando el botón "+ Nueva"</p>
          </div>
        ) : (
          transactions.map(tx => (
            <div key={tx.id} className={`transaction-item ${tx.type}`}>
              <div className="tx-info">
                <span className="tx-category">{tx.category}</span>
                <span className="tx-description">{tx.description}</span>
                <div className="tx-meta">
                  <span className="tx-date">{tx.date}</span>
                  <span className="tx-account">🏦 {getAccountName(tx.accountId)}</span>
                </div>
              </div>
              <div className="tx-actions">
                <span className="tx-amount">
                  {tx.type === 'ingreso' ? '+' : '-'}${tx.amount.toFixed(2)}
                </span>
                <button onClick={() => handleEdit(tx)} className="edit-btn">✏️</button>
                <button onClick={() => handleDelete(tx)} className="delete-btn">🗑️</button>
              </div>
            </div>
          ))
        )}
      </div>

      <a href="/dashboard" className="back-link">← Volver al Dashboard</a>
    </div>
  );
};

export default Transactions;
